import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteFooter from '../../components/feature/SiteFooter';
import SiteNav from '../../components/feature/SiteNav';
import PageHero, { gradientTextStyle } from '../../components/feature/PageHero';
import { trackPhoneConversion } from '../../utils/analytics';

const GRADIENT = 'linear-gradient(135deg, #14b8a6 0%, #10b981 100%)';
const GRAD_STYLE = gradientTextStyle;

// ... existing FAQItem, FAQCategory interfaces and faqData array stay the same ...

interface FAQItem {
  q: string;
  a: string;
}

interface FAQCategory {
  label: string;
  icon: string;
  items: FAQItem[];
}

const faqData: FAQCategory[] = [
  {
    label: 'Preise & Kosten',
    icon: 'ri-price-tag-3-line',
    items: [
      {
        q: 'Was kostet eine Website bei Seitenschmied?',
        a: 'Die Einrichtungsgebühr liegt je nach Umfang der Website zwischen 699 € und 1.499 €. Dazu kommt eine monatliche Rate für laufenden Support und Betreuung – ab 59 €/Monat. Alle Preise zzgl. MwSt.',
      },
      {
        q: 'Wovon hängt der Preis ab?',
        a: 'Ausschließlich vom Umfang der Website – also wie viele Seiten, welche Funktionen, wie komplex die Struktur ist. Nicht von der Branche oder Unternehmensgröße. Ein klarer Auftritt mit einer Kernbotschaft kostet weniger als eine Website mit mehreren Leistungsbereichen, Galerie, Blog und Buchungssystem. Was Sie brauchen, klären wir gemeinsam im Erstgespräch.',
      },
      {
        q: 'Was ist in der monatlichen Rate enthalten?',
        a: 'Laufender Support, regelmäßige Updates und technische Betreuung. Je nach gewähltem Umfang auch Hosting auf DSGVO-konformen Servern in Deutschland sowie rechtssichere Texte (Impressum, Datenschutz). Sie zahlen einmal für die Erstellung – und bekommen dauerhaft Unterstützung.',
      },
      {
        q: 'Wie lange ist die Mindestlaufzeit?',
        a: 'Die Mindestlaufzeit beträgt 12 Monate. Danach verlängert sich die Betreuung automatisch um jeweils einen weiteren Monat – jederzeit kündbar mit entsprechender Frist.',
      },
      {
        q: 'Gibt es versteckte Kosten?',
        a: 'Nein. Alles wird vor der Umsetzung transparent besprochen. Was vereinbart ist, gilt. Keine Überraschungsrechnungen.',
      },
      {
        q: 'Was ist das „Extra", das inklusive ist?',
        a: 'Jede Website enthält ein frei wählbares Extra – zum Beispiel eine Bildgalerie, eine interaktive Standortkarte, ein Kontaktformular mit individuellen Feldern oder aufbereiteten Branchen-Content. Das wird im Erstgespräch gemeinsam festgelegt.',
      },
    ],
  },
  {
    label: 'Ablauf & Erstellung',
    icon: 'ri-road-map-line',
    items: [
      {
        q: 'Wie lange dauert die Erstellung meiner Website?',
        a: 'Typischerweise 2 bis 4 Wochen ab dem Erstgespräch. Die genaue Dauer hängt davon ab, wie schnell Inhalte (Texte, Bilder, Infos) bereitgestellt werden. Wer zügig mitarbeitet, ist schnell online.',
      },
      {
        q: 'Wie läuft das Erstgespräch ab?',
        a: 'Das Erstgespräch ist kostenlos und unverbindlich – per Telefon oder Video-Call, ca. 30 Minuten. Dabei geht es um Ihr Unternehmen, Ihre Ziele und was Ihre Website leisten soll. Sie müssen nichts vorbereiten. Am Ende gibt es eine klare Einschätzung und ein konkretes Angebot.',
      },
      {
        q: 'Was muss ich selbst liefern?',
        a: 'Im Idealfall: Informationen zu Ihrem Unternehmen, Ihren Leistungen und ein paar gute Fotos. Texte werden bei Seitenschmied von Grund auf konzipiert und geschrieben – das ist Teil der Leistung. Wer keine Fotos hat, bekommt passende Bildvorschläge.',
      },
      {
        q: 'Kann ich Änderungen einbringen?',
        a: 'Ja, selbstverständlich. Nach dem ersten Entwurf gibt es eine Feedbackrunde. Änderungswünsche werden besprochen und eingearbeitet. Ziel ist, dass die Website wirklich zu Ihnen passt – nicht nur technisch, sondern auch in Ton und Wirkung.',
      },
    ],
  },
  {
    label: 'Leistungen & Inhalte',
    icon: 'ri-layout-line',
    items: [
      {
        q: 'Was genau macht Seitenschmied?',
        a: 'Seitenschmied erstellt professionelle Websites für kleine und mittlere Unternehmen – von der Konzeption über Texte und Design bis zur technischen Umsetzung und laufenden Betreuung. Der Unterschied: Kommunikation und Botschaft stehen im Mittelpunkt, nicht nur das Aussehen.',
      },
      {
        q: 'Werden auch Texte erstellt?',
        a: 'Ja – und das ist ein wesentlicher Teil der Leistung. Texte werden nicht einfach eingefüllt, sondern konzipiert: Welche Botschaft braucht die Zielgruppe? Was soll der Besucher verstehen, fühlen, tun? Das ist der kommunikative Mehrwert von Seitenschmied.',
      },
      {
        q: 'Sind SEO-Optimierungen enthalten?',
        a: 'Ja. Jede Website wird technisch sauber aufgebaut, mit sinnvollen Seitenstrukturen, beschreibenden Überschriften, Meta-Daten und lokalem SEO-Fokus. Wer bei Google gefunden werden will, ist hier richtig.',
      },
      {
        q: 'Sind Impressum und Datenschutz inklusive?',
        a: 'Das hängt vom gewählten Umfang ab. Rechtstexte (Impressum, Datenschutz) können als laufend aktuell gehaltener Service hinzugebucht werden – oder sind je nach Paket bereits enthalten. Im Erstgespräch wird das klar geregelt.',
      },
      {
        q: 'Welche Extras stehen zur Auswahl?',
        a: 'Unter anderem: interaktive Google-Maps-Karte, Bildgalerie, Buchungsformulare, Bewertungsintegration (Google Reviews), Blogsystem, Mehrsprachigkeit, Pop-ups, animierte Sektionen oder strukturierter Branchen-Content. Der Fantasie sind dabei keine Grenzen gesetzt – von der Einbindung eines Online-Shops für Kleidung über einen Lieferservice für einen Getränkemarkt bis hin zu umfangreichen Contentseiten, die komplexe Themen verständlich erklären.',
      },
    ],
  },
  {
    label: 'Technik & Hosting',
    icon: 'ri-server-line',
    items: [
      {
        q: 'Wo wird die Website gehostet?',
        a: 'Auf performanten, DSGVO-konformen Servern in Deutschland. Hosting kann optional hinzugebucht oder ist je nach vereinbartem Umfang bereits enthalten.',
      },
      {
        q: 'Kann ich meine derzeitige URL/Domain mitnehmen?',
        a: 'Klar! Ein Domainumzug ist das Unkomplizierteste überhaupt. Wer bereits eine Domain hat, behält sie einfach – sie wird auf die neue Website umgezogen. Wer noch keine hat, bekommt Empfehlungen und Unterstützung bei der Registrierung. Da müssen Sie sich um nichts kümmern.',
      },
      {
        q: 'Ist die Website mobiloptimiert?',
        a: 'Ja, selbstverständlich. Alle Websites von Seitenschmied sind vollständig responsive – für Smartphones, Tablets und Desktop optimiert. 63 % aller Websuchen kommen heute vom Handy. Das wird von Anfang an berücksichtigt.',
      },
      {
        q: 'Ist ein SSL-Zertifikat enthalten?',
        a: 'Ja. Alle Websites werden mit SSL-Zertifikat ausgeliefert – sichere Verbindung (https), kein Sicherheitshinweis im Browser, und ein positives Signal für Google.',
      },
      {
        q: 'Was passiert, wenn ich die Betreuung beende?',
        a: 'Nach der Mindestlaufzeit von 12 Monaten kann monatlich gekündigt werden. Die Website bleibt in Ihrem Besitz – Sie können sie auf Ihrem eigenen Hosting weiterführen. Details besprechen wir transparent im Erstgespräch.',
      },
      {
        q: 'Kann ich auch selbst hosten?',
        a: 'Ja, sehr gerne sogar. Wer bereits über ein eigenes Hosting verfügt, kann die Website dort betreiben. Das ändert nichts an Konzeption, Design und Umsetzung – nur der Hosting-Teil entfällt aus der monatlichen Rate. Details klären wir im Erstgespräch.',
      },
    ],
  },
  {
    label: 'Support & Betreuung',
    icon: 'ri-customer-service-2-line',
    items: [
      {
        q: 'Wie funktioniert der Support?',
        a: 'Je nach vereinbartem Umfang: E-Mail-Support mit Updates pro Jahr bis hin zu monatlichem Support-Kontingent für Änderungen, Fragen und Anpassungen. Support heißt bei Seitenschmied: echte Antworten von einem echten Menschen – nicht automatisiert.',
      },
      {
        q: 'Was passiert, wenn ich etwas auf der Website ändern möchte?',
        a: 'Änderungswünsche werden über das enthaltene Support-Kontingent abgewickelt. Kleine Anpassungen (Texte, Bilder, Öffnungszeiten) sind schnell erledigt. Größere Änderungen werden nach Aufwand besprochen.',
      },
      {
        q: 'Bin ich langfristig auf Seitenschmied angewiesen?',
        a: 'Nein. Seitenschmied ist auf Partnerschaft ausgelegt, nicht auf Abhängigkeit. Die Website gehört Ihnen. Wer möchte, kann nach der Laufzeit alles selbst weiterführen. Wer weiter betreut werden möchte, bleibt einfach dabei.',
      },
    ],
  },
];

function AccordionItem({ item, isOpen, onToggle }: { item: FAQItem; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="border-b last:border-0" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
      <button
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-6 py-6 text-left cursor-pointer group"
      >
        <span
          className="text-base font-semibold leading-snug group-hover:opacity-80 transition-opacity"
          style={{ color: '#fff', fontFamily: "'Outfit', sans-serif" }}
        >
          {item.q}
        </span>
        <span
          className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300"
          style={{ background: isOpen ? GRADIENT : 'rgba(255,255,255,0.08)', color: '#fff' }}
        >
          <i className={`ri-${isOpen ? 'subtract' : 'add'}-line text-sm`}></i>
        </span>
      </button>
      <div
        className="overflow-hidden transition-all duration-300"
        style={{ maxHeight: isOpen ? '400px' : '0px', opacity: isOpen ? 1 : 0 }}
      >
        <p
          className="text-base leading-relaxed pb-6"
          style={{ color: 'rgba(255,255,255,0.60)', fontFamily: "'Inter', sans-serif" }}
        >
          {item.a}
        </p>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState(0);
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleItem = (catIdx: number, itemIdx: number) => {
    const key = `${catIdx}-${itemIdx}`;
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCategoryClick = (idx: number) => {
    setActiveCategory(idx);
    const el = document.getElementById(`faq-cat-${idx}`);
    if (el) {
      const offset = 112;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const totalQuestions = faqData.reduce((sum, cat) => sum + cat.items.length, 0);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>

      <SiteNav activePage="faq" navId="faq-nav" />

      <PageHero
        label="Häufige Fragen"
        headline={
          <>
            Was Sie<br />wissen<br />
            <span style={GRAD_STYLE}>wollen.</span>
          </>
        }
        subtext="Alle wichtigen Fragen zu Preisen, Ablauf, Leistungen und Support – klar und ehrlich beantwortet."
        backgroundImage="https://readdy.ai/api/search-image?query=Extreme%20close-up%20of%20skilled%20craftsman%20hands%20resting%20over%20a%20sleek%20dark%20mechanical%20keyboard%20on%20a%20black%20desk%2C%20warm%20amber%20and%20teal%20studio%20lighting%20creating%20dramatic%20shadows%20and%20highlights%20across%20the%20hands%20and%20keys%2C%20very%20shallow%20depth%20of%20field%20with%20beautifully%20blurred%20dark%20background%2C%20fine%20skin%20texture%20and%20knuckle%20detail%20visible%2C%20moody%20cinematic%20atmosphere%2C%20rich%20contrast%20between%20warm%20golden%20light%20on%20hands%20and%20cool%20dark%20surroundings%2C%20professional%20editorial%20photography%2C%20analog%20film%20grain%2C%20intimate%20and%20focused%20composition%2C%20no%20face%20visible%20only%20hands%20and%20keyboard&width=1920&height=1080&seq=faq-hero-craft-digital-v1&orientation=landscape"
        backgroundPosition="center"
        overlayClasses="from-black/80 via-black/65 to-[#0e0e0e]"
      >
        {/* Question count badge */}
        <div className="flex items-center gap-2 mt-8 sm:mt-10 text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
          <i className="ri-question-line"></i>
          <span>{totalQuestions} Fragen in {faqData.length} Kategorien</span>
        </div>
      </PageHero>

      {/* Main FAQ Content */}
      <section className="bg-[#0e0e0e] pb-14 sm:pb-24 lg:pb-32 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-[280px_1fr] gap-8 lg:gap-12">

            {/* Category Sidebar */}
            <div className="lg:sticky lg:top-28 h-fit">
              {/* Mobile horizontal scroll on small screens */}
              <nav className="flex lg:flex-col gap-2 overflow-x-auto pb-3 lg:pb-0 lg:space-y-1 -mx-4 px-4 lg:mx-0 lg:px-0">
                {faqData.map((cat, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleCategoryClick(idx)}
                    className="flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-xl text-left transition-all duration-200 cursor-pointer flex-shrink-0 lg:flex-shrink"
                    style={{ background: activeCategory === idx ? GRADIENT : 'transparent', color: activeCategory === idx ? '#fff' : 'rgba(255,255,255,0.45)' }}
                  >
                    <span className="w-7 h-7 lg:w-8 lg:h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: activeCategory === idx ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.06)' }}>
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    <span className="text-xs lg:text-sm font-semibold whitespace-nowrap" style={{ fontFamily: "'Outfit', sans-serif" }}>{cat.label}</span>
                    <span className="ml-auto text-xs font-bold hidden lg:inline" style={{ color: activeCategory === idx ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)' }}>
                      {cat.items.length}
                    </span>
                  </button>
                ))}
              </nav>

              {/* Sidebar CTA */}
              <div className="hidden lg:block mt-8 p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <p className="text-xs font-bold tracking-[0.2em] uppercase mb-3" style={{ color: '#14b8a6' }}>
                  Frage nicht dabei?
                </p>
                <p className="text-sm text-white/50 leading-relaxed mb-4">
                  Rufen Sie einfach an – keine Warteschleife, kein Formular-Dschungel.
                </p>
                <a href="tel:+491607676763" onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                  <button className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold rounded-lg text-white transition-opacity hover:opacity-80 cursor-pointer whitespace-nowrap" style={{ background: GRADIENT }}>
                    <i className="ri-phone-line"></i>
                    Jetzt anrufen
                  </button>
                </a>
              </div>
            </div>

            {/* FAQ Accordions */}
            <div className="pt-2 lg:pt-0">
              {faqData.map((cat, catIdx) => (
                <div
                  key={catIdx}
                  id={`faq-cat-${catIdx}`}
                  className={`mb-12 sm:mb-16 transition-all duration-300 ${activeCategory === catIdx ? 'opacity-100' : 'opacity-30'}`}
                >
                  <div className="flex items-center gap-3 lg:gap-4 mb-6 lg:mb-8">
                    <div className="w-9 h-9 lg:w-10 lg:h-10 flex items-center justify-center rounded-xl" style={{ background: GRADIENT }}>
                      <i className={`${cat.icon} text-sm lg:text-base text-white`}></i>
                    </div>
                    <h2 className="text-xl lg:text-2xl font-black text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {cat.label}
                    </h2>
                  </div>
                  <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="px-5 sm:px-8">
                      {cat.items.map((item, itemIdx) => (
                        <AccordionItem
                          key={itemIdx}
                          item={item}
                          isOpen={!!openItems[`${catIdx}-${itemIdx}`]}
                          onToggle={() => { setActiveCategory(catIdx); toggleItem(catIdx, itemIdx); }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-0 lg:flex lg:flex-wrap lg:items-center lg:justify-between">
            {[
              { value: 'Aus 1 Hand', sub1: 'Konzept, Texte &', sub2: 'Programmierung' },
              { value: '24/7', sub1: 'Ihre Website', sub2: 'arbeitet für Sie' },
              { value: '12', sub1: 'Monate', sub2: 'Mindestlaufzeit' },
              { value: '1', sub1: 'Extra', sub2: 'in jedem Umfang inklusive' },
            ].map((stat, idx) => (
              <>
                {idx > 0 && <div key={`d-${idx}`} className="w-px h-12 bg-gray-200 hidden lg:block" />}
                <div key={idx} className="flex items-center gap-3 lg:gap-4">
                  <span className="text-2xl sm:text-3xl lg:text-4xl font-black" style={{ fontFamily: "'Outfit', sans-serif", ...GRAD_STYLE }}>
                    {stat.value}
                  </span>
                  <div>
                    <p className="text-xs lg:text-sm text-gray-400 font-medium">{stat.sub1}</p>
                    <p className="text-xs lg:text-sm text-gray-600 font-semibold">{stat.sub2}</p>
                  </div>
                </div>
              </>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 sm:py-24 lg:py-32 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-end">
            <div>
              <p className="ss-label" style={{ color: '#14b8a6' }}>
                Nächster Schritt
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-6xl xl:text-7xl font-black text-gray-900 leading-[1.05] mb-6 sm:mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Noch eine<br />offene Frage?<br /><span style={GRAD_STYLE}>Rufen Sie an.</span>
              </h2>
              <p className="text-sm sm:text-base lg:text-lg text-gray-500 leading-relaxed mb-6 sm:mb-8 max-w-md">
                Das Erstgespräch kostet nichts. Keine Verpflichtung, keine Vorbereitung nötig. Einfach anrufen – und in 30 Minuten wissen Sie, was zu tun ist.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+491607676763" onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                  <button
                    className="flex items-center gap-2 px-8 sm:px-10 py-4 sm:py-5 font-bold text-base lg:text-lg rounded-xl text-white hover:scale-105 hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    style={{ background: GRADIENT, boxShadow: '0 10px 40px rgba(16,185,129,0.25)' }}
                  >
                    <i className="ri-phone-line text-lg"></i>
                    Jetzt anrufen
                  </button>
                </a>
                <Link to="/termin-buchen">
                  <button className="px-8 sm:px-10 py-4 sm:py-5 font-bold text-base lg:text-lg rounded-xl border-2 border-gray-200 text-gray-700 hover:border-gray-400 transition-colors whitespace-nowrap cursor-pointer">
                    Termin buchen
                  </button>
                </Link>
              </div>
            </div>
            <div className="bg-[#0e0e0e] ss-card-pad">
              <p className="text-sm font-bold tracking-[0.2em] uppercase mb-6 sm:mb-8" style={{ color: '#14b8a6' }}>Das Erstgespräch</p>
              <div className="space-y-5 sm:space-y-6">
                {[
                  { icon: 'ri-time-line', text: 'Ca. 30 Minuten – kompakt und auf den Punkt.' },
                  { icon: 'ri-chat-3-line', text: 'Per Telefon oder Video-Call – ganz wie es passt.' },
                  { icon: 'ri-draft-line', text: 'Sie bekommen eine klare Einschätzung und ein konkretes Angebot.' },
                  { icon: 'ri-heart-line', text: 'Kostenlos und unverbindlich – versprochen.' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: GRADIENT }}>
                      <i className={`${item.icon} text-sm text-white`}></i>
                    </div>
                    <p className="text-gray-300 text-sm sm:text-base leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
